import React, { useRef, useState } from 'react';

import { useNotification } from '../../hooks/useNotification';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ClearIcon from '@mui/icons-material/Clear';

const MAX_FILES = 5;
const MAX_SIZE_MB = 50;

export const MultiFileUpload = ({ config, files, onChange }) => {
    const [sendNotification] = useNotification();
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const bytes = new Uint8Array(reader.result);
                const base64 = btoa(bytes.reduce((data, byte) => data + String.fromCharCode(byte), ''));
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });

    const processFiles = async (selectedFiles) => {
        if (!selectedFiles.length) return;
        if (files.length + selectedFiles.length > MAX_FILES) {
            sendNotification({ msg: `Máximo ${MAX_FILES} archivos permitidos`, variant: 'error' });
            return;
        }
        const existingNames = files.map(f => f.name.toLowerCase());
        const processedFiles = [];
        for (const file of selectedFiles) {
            const normalizedName = file.name.replace(/\s+/g, '_');
            const lowerName = normalizedName.toLowerCase();
            if (existingNames.includes(lowerName) || processedFiles.some(f => f.name.toLowerCase() === lowerName)) {
                sendNotification({ msg: `El archivo ${file.name} ya fue agregado`, variant: 'error'});
                continue;
            }
            if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                sendNotification({ msg: `El archivo ${file.name} supera los ${MAX_SIZE_MB}MB`, variant: 'error' });
                continue;
            }
            const base64 = await fileToBase64(file);
            processedFiles.push({
                name: file.name.replace(/\s+/g, '_'),
                type: file.type,
                size: file.size,
                base64,
            });
            existingNames.push(lowerName); 
        }
        if (processedFiles.length > 0) {
            onChange([...files, ...processedFiles]);
        }
    };

    const handleSelectFiles = async (event) => {
        await processFiles(Array.from(event.target.files || []));
        event.target.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        await processFiles(Array.from(e.dataTransfer.files || []));
    };

    const handleRemove = (index) => onChange(files.filter((_, i) => i !== index));

    return (
        <>
            {config?.label && <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>{config.label}</Typography>}
            <Paper variant="outlined"
                sx={{
                    p: 3,
                    borderRadius: 3,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: isDragging ? 'primary.main' : 'primary.light',
                    backgroundColor: isDragging ? 'primary.50' : 'rgba(103,40,175,0.03)',
                    cursor: 'pointer',
                    padding: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': { backgroundColor: 'rgba(103,40,175,0.06)' }
                }}
                onClick={() => inputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <Box sx={{ textAlign: 'center', mb: files.length > 0 ? 3 : 0 }}>
                    <UploadFileIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.8 }} />
                    <Typography variant="body2"
                        sx={{
                            mt: 1,
                            color: 'primary.main',
                            fontWeight: 500,
                            opacity: 0.9
                        }}
                    >
                        {files.length === 0
                            ? 'Arrastra archivos aquí o haz clic para seleccionar'
                            : 'Haz clic o arrastra para agregar más archivos'}
                    </Typography>
                </Box>

                {files.length > 0 && (
                    <Box
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}
                    >
                        {files.map((file, index) => (
                            <Paper key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: 'primary.main',
                                    color: '#fff',
                                    borderRadius: 5,
                                    pl: 2,
                                    pr: 1,
                                    py: 0.5,
                                    fontSize: 13,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { backgroundColor: 'primary.dark'}
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Typography variant="body2" sx={{mr: 1,fontWeight: 500,fontSize: 13}}>{file.name}</Typography>
                                <Tooltip title="Eliminar">
                                    <IconButton size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemove(index);
                                        }}
                                        sx={{
                                            color: '#fff',
                                            p: 0.5,
                                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                                        }}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Paper>

            <input multiple hidden type="file"
                accept=".pdf, .xml, .eml, .zip, .jpg, .png"
                ref={inputRef}
                onChange={handleSelectFiles}
            />
        </>
    );

};